// src/api/clients.ts
import api from "./api";

export interface ClientBranch {
  id?: string;
  branchName: string;
  gstin?: string | null;
  address1?: string | null;
  address2?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
  linked_branch_id?: string | null;
}

export interface Client {
  id: string;
  clientName: string;
  email: string | null;
  phone: string | null;
  address1: string | null;
  address2: string | null;
  state: string | null;
  country: string | null;
  pincode: string | null;
  gstin: string | null;
  pan: string | null;
  user_id: string;
  branchID: string | null;
  branches?: ClientBranch[];
  // Cross-org link metadata. When `is_linked` is true the row mirrors the
  // basic_details profile of another Teamlio org and is read-only here.
  linked_headOfficeID?: string | null;
  linked_user_id?: string | null;
  is_linked?: boolean;
}

export interface ClientCreatePayload {
  clientName: string;
  email?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  state?: string;
  country?: string;
  pincode?: string;
  gstin?: string;
  pan?: string;
  branchDetails?: ClientBranch[];
}

export interface ClientUpdatePayload {
  clientName?: string;
  email?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  state?: string;
  country?: string;
  pincode?: string;
  gstin?: string;
  pan?: string;
  branchDetails?: ClientBranch[];
}

export interface GstinLookupResult {
  gstin: string;
  pan: string;
  state_code: string;
  state: string;
  country: string;
  source: "local" | "external";
  note?: string;
}

export const clientsApi = {
  list: (params?: { search?: string; page?: number; page_size?: number }) =>
    api.get("/clients", { params }),

  get: (clientId: string) =>
    api.get(`/clients/${clientId}`),

  create: (payload: ClientCreatePayload) =>
    api.post("/clients", payload),

  // Stateless GSTIN parser — derives state + PAN + country from a GSTIN so
  // the quick-add modals can pre-fill those fields. Available at both
  // /clients/gstin-lookup/{gstin} and /vendors/gstin-lookup/{gstin}; we
  // call the clients surface from both modals since the response is identical.
  lookupGstin: (gstin: string) =>
    api.get<GstinLookupResult>(`/clients/gstin-lookup/${encodeURIComponent(gstin)}`),

  update: (clientId: string, payload: ClientUpdatePayload) =>
    api.patch(`/clients/${clientId}`, payload),

  // Compose-and-send an email to the client from the detail view. Sent from
  // Teamlio's default address (noreply) via the backend SendGrid helper.
  sendEmail: (clientId: string, payload: { subject: string; body: string }) =>
    api.post(`/clients/${clientId}/send-email`, payload),

  delete: (clientId: string) =>
    api.delete(`/clients/${clientId}`),

  settings: () =>
    api.get('/get_startup_khata'),

  importClients: (payload: any[]) =>
    api.post('/clients/import_clients', payload),

  importExcel: (formData: FormData) =>
    api.post("/clients/import_client_excel", formData),
};
